import type {
  Answers,
  DocumentType,
  PeopleData,
  PeopleIssue,
  PeopleRule,
  PeopleSheet,
  TemplateConfig,
} from '@lg/shared';
import {
  computeCoverage,
  coverageAmounts,
  limitKey,
  type CoverageMatrix,
  type RoutingHolder,
  type RoutingPeople,
  type RoutingPosition,
} from '../routing.js';

/** A position that already exists in D365 (regional, corporate or the venue itself). */
export interface ExistingPosition {
  positionId: string;
  title: string;
  jobId: string;
  companyId: string | null;
  reportsToPositionId: string | null;
  holder: RoutingHolder | null;
}

/**
 * Everything P2–P8 need from outside the file, fetched up front so the rules
 * stay pure (the service gathers it from D365 and the directory).
 */
export interface PeopleContext {
  mode: 'newVenue' | 'existingVenue';
  companyId: string;
  template: TemplateConfig;
  answers: Answers | null;
  /** UPN → directory status; null = not found (P2) */
  directory: ReadonlyMap<string, { displayName: string; enabled: boolean } | null>;
  /** D365 positions referenced by the file or above it, keyed by ID */
  existingPositions: ReadonlyMap<string, ExistingPosition>;
  /** Position IDs / personnel numbers already used by another company (P7) */
  positionIdsElsewhere: ReadonlySet<string>;
  personnelNumbersElsewhere: ReadonlySet<string>;
  /** Users, workers and limits already in the venue company (existing-venue mode) */
  existingUserUpns: ReadonlySet<string>;
  existingPersonnelNumbers: ReadonlySet<string>;
  /** Limits for jobs outside the file (regional, corporate, or existing venue) */
  baseLimits: ReadonlyArray<{ jobId: string; documentType: DocumentType; amountUsd: number }>;
  /** Role holders already in D365 for this company (e.g. LEDGERFINANCIALCONTROLLER) */
  existingRoleHolders: ReadonlyArray<{ roleId: string; name: string; upn: string }>;
}

export interface PeopleValidation {
  issues: PeopleIssue[];
  coverage: CoverageMatrix;
  errorCount: number;
  warningCount: number;
}

const usd = (n: number): string => `$${n.toLocaleString('en-US')}`;

export function buildRoutingPeople(data: PeopleData, ctx: PeopleContext): RoutingPeople {
  const workerByNumber = new Map(data.workers.map((w) => [w.personnelNumber, w]));
  const nameByUpn = new Map<string, string>();
  for (const u of data.users) nameByUpn.set(u.upn, u.displayName);
  for (const w of data.workers) nameByUpn.set(w.upn, `${w.firstName} ${w.lastName}`);

  const positions = new Map<string, RoutingPosition>();
  for (const p of ctx.existingPositions.values()) {
    positions.set(p.positionId, { ...p, external: true });
  }
  const holderOf = new Map<string, RoutingHolder>();
  const primaryPositionByUpn = new Map<string, string>();
  for (const a of data.assignments) {
    const w = workerByNumber.get(a.personnelNumber);
    if (!w) continue;
    if (!holderOf.has(a.positionId)) {
      holderOf.set(a.positionId, {
        name: `${w.firstName} ${w.lastName}`,
        upn: w.upn,
        personnelNumber: w.personnelNumber,
      });
    }
    if (a.primary) primaryPositionByUpn.set(w.upn, a.positionId);
  }
  for (const p of data.positions) {
    positions.set(p.positionId, {
      positionId: p.positionId,
      title: p.title,
      jobId: p.jobId,
      reportsToPositionId: p.reportsToPositionId,
      holder: holderOf.get(p.positionId) ?? null,
      external: false,
    });
  }

  const limits = new Map<string, number>();
  for (const l of ctx.baseLimits) limits.set(limitKey(l.jobId, l.documentType), l.amountUsd);
  for (const l of data.approvalLimits) limits.set(limitKey(l.jobId, l.documentType), l.amountUsd);

  const roleHolders = new Map<string, Array<{ name: string; upn: string }>>();
  const addHolder = (roleId: string, upn: string, name: string): void => {
    const list = roleHolders.get(roleId) ?? [];
    if (!list.some((h) => h.upn === upn)) list.push({ name, upn });
    roleHolders.set(roleId, list);
  };
  for (const r of ctx.existingRoleHolders) addHolder(r.roleId, r.upn, r.name);
  for (const r of data.roles) addHolder(r.roleId, r.upn, nameByUpn.get(r.upn) ?? r.upn);

  return { positions, primaryPositionByUpn, limits, roleHolders };
}

export function validatePeople(data: PeopleData, ctx: PeopleContext): PeopleValidation {
  const issues: PeopleIssue[] = [];
  const add = (
    rule: PeopleRule,
    sheet: PeopleSheet | null,
    rowNumber: number | null,
    column: string | null,
    message: string,
    severity: 'error' | 'warning' = 'error',
  ): void => {
    issues.push({ rule, sheet, rowNumber, column, severity, message });
  };

  const userUpns = new Set([...data.users.map((u) => u.upn), ...ctx.existingUserUpns]);
  const filePositionIds = new Set(data.positions.map((p) => p.positionId));
  const workerNumbers = new Set([
    ...data.workers.map((w) => w.personnelNumber),
    ...ctx.existingPersonnelNumbers,
  ]);
  const jobIds = new Set(ctx.template.jobCatalog.map((j) => j.jobId));
  const roleById = new Map(ctx.template.roleCatalog.map((r) => [r.roleId, r]));

  // P2 — accounts exist and are enabled in Entra ID
  for (const u of data.users) {
    const entry = ctx.directory.get(u.upn);
    if (!entry) add('P2', 'users', u.rowNumber, 'upn', `${u.upn} was not found in Entra ID`);
    else if (!entry.enabled) add('P2', 'users', u.rowNumber, 'upn', `${u.upn} is disabled in Entra ID`);
  }
  for (const w of data.workers) {
    if (!userUpns.has(w.upn)) add('P2', 'workers', w.rowNumber, 'upn', `${w.upn} is not in the users sheet`);
  }
  for (const r of data.roles) {
    if (!userUpns.has(r.upn)) add('P2', 'roles', r.rowNumber, 'upn', `${r.upn} is not in the users sheet`);
  }

  // P3 — reporting lines
  const positionExists = (id: string): boolean => filePositionIds.has(id) || ctx.existingPositions.has(id);
  for (const p of data.positions) {
    if (p.reportsToPositionId === p.positionId) {
      add('P3', 'positions', p.rowNumber, 'reportsToPositionId', 'A position cannot report to itself');
    } else if (!positionExists(p.reportsToPositionId)) {
      add(
        'P3',
        'positions',
        p.rowNumber,
        'reportsToPositionId',
        `Reports-to position ${p.reportsToPositionId} does not exist in the file or in D365`,
      );
    }
  }
  const parentOf = new Map(data.positions.map((p) => [p.positionId, p.reportsToPositionId]));
  const inCycle = new Set<string>();
  for (const p of data.positions) {
    const path: string[] = [];
    let cur: string | undefined = p.positionId;
    while (cur && parentOf.has(cur) && !path.includes(cur)) {
      path.push(cur);
      cur = parentOf.get(cur);
    }
    if (cur && path.includes(cur) && cur !== parentOf.get(cur)) {
      for (const id of path.slice(path.indexOf(cur))) inCycle.add(id);
    }
  }
  for (const p of data.positions) {
    if (inCycle.has(p.positionId)) {
      add(
        'P3',
        'positions',
        p.rowNumber,
        'reportsToPositionId',
        `Reporting line loops back to ${p.positionId}`,
      );
    }
  }
  if (ctx.mode === 'newVenue' && data.positions.length > 0) {
    // Positions whose manager is missing entirely were reported above; they are not "top" positions.
    const tops = data.positions.filter(
      (p) => !filePositionIds.has(p.reportsToPositionId) && ctx.existingPositions.has(p.reportsToPositionId),
    );
    if (tops.length === 0 && inCycle.size === 0) {
      add('P3', 'positions', null, null, 'The venue has no top position reporting to regional or corporate');
    }
    // Several venue positions may report outside the venue (the GM to the regional director, the
    // GL accountant to the finance director); each must report to a regional or corporate one.
    for (const t of tops) {
      const parent = ctx.existingPositions.get(t.reportsToPositionId);
      if (parent && parent.companyId !== null) {
        add(
          'P3',
          'positions',
          t.rowNumber,
          'reportsToPositionId',
          `The top position must report to a regional or corporate position; ${parent.positionId} belongs to ${parent.companyId}`,
        );
      }
    }
  }

  // P4 — assignments
  const primaryCount = new Map<string, number>();
  const holdersPerPosition = new Map<string, number>();
  for (const a of data.assignments) {
    if (!positionExists(a.positionId)) {
      add('P4', 'assignments', a.rowNumber, 'positionId', `Position ${a.positionId} does not exist`);
    }
    if (!workerNumbers.has(a.personnelNumber)) {
      add(
        'P4',
        'assignments',
        a.rowNumber,
        'personnelNumber',
        `Worker ${a.personnelNumber} is not in the workers sheet`,
      );
    }
    if (a.primary) primaryCount.set(a.personnelNumber, (primaryCount.get(a.personnelNumber) ?? 0) + 1);
    holdersPerPosition.set(a.positionId, (holdersPerPosition.get(a.positionId) ?? 0) + 1);
  }
  for (const w of data.workers) {
    const n = primaryCount.get(w.personnelNumber) ?? 0;
    if (n === 0)
      add(
        'P4',
        'workers',
        w.rowNumber,
        'personnelNumber',
        `${w.firstName} ${w.lastName} has no primary position`,
      );
    if (n > 1)
      add(
        'P4',
        'workers',
        w.rowNumber,
        'personnelNumber',
        `${w.firstName} ${w.lastName} has ${n} primary positions; exactly one is allowed`,
      );
  }
  for (const a of data.assignments) {
    if ((holdersPerPosition.get(a.positionId) ?? 0) > 1) {
      add(
        'P4',
        'assignments',
        a.rowNumber,
        'positionId',
        `Position ${a.positionId} has more than one worker`,
      );
    }
  }
  for (const p of data.positions) {
    if (!holdersPerPosition.has(p.positionId)) {
      add(
        'P4',
        'positions',
        p.rowNumber,
        'positionId',
        `Position ${p.positionId} (${p.title}) is vacant`,
        'warning',
      );
    }
  }

  // P5 — jobs and limits
  for (const p of data.positions) {
    if (!jobIds.has(p.jobId))
      add('P5', 'positions', p.rowNumber, 'jobId', `Job ${p.jobId} is not in the template job catalog`);
  }
  for (const l of data.approvalLimits) {
    if (!jobIds.has(l.jobId))
      add('P5', 'approvalLimits', l.rowNumber, 'jobId', `Job ${l.jobId} is not in the template job catalog`);
  }
  const limitKeys = new Set([
    ...data.approvalLimits.map((l) => limitKey(l.jobId, l.documentType)),
    ...(ctx.mode === 'existingVenue' ? ctx.baseLimits.map((l) => limitKey(l.jobId, l.documentType)) : []),
  ]);
  const inScopeDocs = ctx.template.routing.documentTypes.filter((d) => d.inScope).map((d) => d.type);
  const usedJobs = [...new Set(data.positions.map((p) => p.jobId))].filter((j) => jobIds.has(j));
  for (const jobId of usedJobs) {
    for (const doc of inScopeDocs) {
      if (!limitKeys.has(limitKey(jobId, doc))) {
        add(
          'P5',
          'approvalLimits',
          null,
          'amountUsd',
          `Job ${jobId} has no ${doc} approval limit (use 0 for "cannot approve")`,
        );
      }
    }
  }

  // P6 — roles and segregation of duties
  const rolesByUpn = new Map<string, string[]>();
  for (const r of data.roles) {
    if (!roleById.has(r.roleId)) {
      add('P6', 'roles', r.rowNumber, 'roleId', `Role ${r.roleId} is not in the venue role catalog`);
      continue;
    }
    const held = rolesByUpn.get(r.upn) ?? [];
    for (const other of held) {
      const conflicts = roleById.get(r.roleId)?.sodConflicts ?? [];
      if (conflicts.includes(other)) {
        add(
          'P6',
          'roles',
          r.rowNumber,
          'roleId',
          `${r.upn} would hold both ${other} and ${r.roleId}, a segregation-of-duties conflict`,
        );
      }
    }
    held.push(r.roleId);
    rolesByUpn.set(r.upn, held);
  }

  // P7 — IDs not used by another company
  for (const p of data.positions) {
    if (ctx.positionIdsElsewhere.has(p.positionId)) {
      add(
        'P7',
        'positions',
        p.rowNumber,
        'positionId',
        `Position ID ${p.positionId} is already used by another company`,
      );
    }
  }
  for (const w of data.workers) {
    if (ctx.personnelNumbersElsewhere.has(w.personnelNumber)) {
      add(
        'P7',
        'workers',
        w.rowNumber,
        'personnelNumber',
        `Personnel number ${w.personnelNumber} is already used by another company`,
      );
    }
  }

  // P8 — routing coverage
  const people = buildRoutingPeople(data, ctx);
  const submitters = [...people.primaryPositionByUpn.keys()];
  const coverage = computeCoverage(
    people,
    ctx.template.routing,
    submitters,
    coverageAmounts(data.approvalLimits),
  );
  // One message per document type, so a single broken line doesn't read as dozens of errors.
  for (const row of coverage.rows) {
    const gaps = row.cells.filter((c) => c.status === 'gap');
    const first = gaps[0];
    if (!first?.example) continue;
    const range =
      gaps.length === 1
        ? usd(first.amountUsd)
        : `${usd(first.amountUsd)} to ${usd(gaps[gaps.length - 1]?.amountUsd ?? 0)}`;
    add(
      'P8',
      null,
      null,
      null,
      `${row.documentType}: ${gaps.length} of ${row.cells.length} amount bands have no approver (${range}). Example from ${first.example.submitterUpn}: ${first.example.message}`,
    );
  }

  // P9 (project manager / controller in users) is deferred with the Projects step.

  return {
    issues,
    coverage,
    errorCount: issues.filter((i) => i.severity === 'error').length,
    warningCount: issues.filter((i) => i.severity === 'warning').length,
  };
}
